import { useState, useContext, useMemo, useRef, useEffect } from "react"
import { useParams, useHistory } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useQuery, useMutation, useQueryClient } from "react-query"
import { ExclamationIcon, TrashIcon, ChevronRightIcon, OfficeBuildingIcon, SearchIcon, RefreshIcon, XIcon } from "@heroicons/react/outline"
import {
  PageTopBar,
  PageView,
  ButtonLoader,
  SelectInput,
  ConfirmDialog,
  Notification,
} from "../../components"
import { Loader } from "../../components/Loader"
import { UserContext } from "../../hooks/UserContext"
import {
  fetchLostLocations,
  fetchLostLocationStockSummary,
  deleteLostStock,
  deleteLostStockByStore
} from "../../services/stockUnitServices"
import { LOCATIONS, DELETED_LOST_HISTORY } from "../../navigation/constants"

const ERROR_MESSAGES = {
  'PRODUCT_NOT_FOUND': 'delete_lost_stock.errors.product_not_found',
  'WAREHOUSE_NOT_FOUND': 'delete_lost_stock.errors.warehouse_not_found',
  'LOT_NOT_FOUND': 'delete_lost_stock.errors.lot_not_found',
  'LOT_DOES_NOT_BELONG_TO_PRODUCT': 'delete_lost_stock.errors.lot_does_not_belong_to_product',
  'LOCATION_NOT_FOUND': 'delete_lost_stock.errors.location_not_found',
  'NO_STOCK_UNITS_FOUND': 'delete_lost_stock.errors.no_stock_units_found',
  'QUANTITY_EXCEEDS_AVAILABLE': 'delete_lost_stock.errors.quantity_exceeds_available',
  'STORE_NOT_FOUND': 'delete_lost_stock.errors.store_not_found',
  'INVALID_PARAMETER': 'delete_lost_stock.errors.invalid_parameter',
  'INTERNAL_ERROR': 'delete_lost_stock.errors.internal_error',
}

export const DeleteLostStockContainer = () => {
  const { warehouse_id } = useParams()
  const history = useHistory()
  const { i18n } = useTranslation()
  const { user } = useContext(UserContext)
  const queryClient = useQueryClient()
  const searchInputRef = useRef(null)
  const selectedLocationRef = useRef(null)

  // State
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [quantities, setQuantities] = useState({}) // { "productId-lotId": quantity }
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [showNotification, setShowNotification] = useState(false)
  const [notificationData, setNotificationData] = useState({ type: 'success', title: '', description: '' })
  const [skuSearch, setSkuSearch] = useState('')
  const [appliedSkuFilter, setAppliedSkuFilter] = useState('')
  const [expandedStores, setExpandedStores] = useState({})
  const [storeToDelete, setStoreToDelete] = useState(null)
  const [confirmStoreDialogOpen, setConfirmStoreDialogOpen] = useState(false)

  const hasPermission = user?.permissions?.delete_lost_stock === true

  // Fetch lost locations
  const { 
    data: locationsData,
    isLoading: isLoadingLocations,
    isError: isLocationsError,
    refetch: refetchLocations
  } = useQuery(
    ["lost-locations", warehouse_id],
    () => fetchLostLocations(warehouse_id),
    { 
      enabled: hasPermission,
      refetchOnWindowFocus: false 
    }
  )

  // Fetch stock summary when location is selected
  const { 
    data: stockSummaryData,
    isLoading: isLoadingStockSummary,
    isFetching: isFetchingStockSummary,
    isError: isStockSummaryError,
    refetch: refetchStockSummary
  } = useQuery(
    ["lost-location-stock-summary", warehouse_id, selectedLocation?.id],
    () => fetchLostLocationStockSummary(warehouse_id, selectedLocation.id),
    {
      enabled: !!selectedLocation?.id,
      refetchOnWindowFocus: false
    }
  )

  // Clamp quantities when stock data changes (e.g. after refetch)
  useEffect(() => {
    if (!stockSummaryData?.stock_items) return
    setQuantities(prev => {
      const updated = { ...prev }
      let changed = false
      stockSummaryData.stock_items.forEach(item => {
        const key = `${item.store_id}-${item.product_id}-${item.lot_id || 'no-lot'}`
        if (updated[key] !== undefined && updated[key] > item.quantity) {
          updated[key] = item.quantity
          changed = true
        }
      })
      // Remove quantities for items that no longer exist
      const validKeys = new Set(
        stockSummaryData.stock_items.map(item => `${item.store_id}-${item.product_id}-${item.lot_id || 'no-lot'}`)
      )
      Object.keys(updated).forEach(key => {
        if (!validKeys.has(key)) {
          delete updated[key]
          changed = true
        }
      })
      return changed ? updated : prev
    })
  }, [stockSummaryData])

  // Keep ref in sync with selectedLocation
  useEffect(() => {
    selectedLocationRef.current = selectedLocation
  }, [selectedLocation])

  // Update selectedLocation with fresh data after locations refetch
  useEffect(() => {
    const current = selectedLocationRef.current
    if (!locationsData?.locations || !current) return
    const freshLocation = locationsData.locations.find(loc => loc.id === current.id)
    if (!freshLocation) {
      // Location no longer exists, reset selection
      setSelectedLocation(null)
      setQuantities({})
      setSkuSearch('')
      setAppliedSkuFilter('')
      return
    }
    // Update with fresh data (e.g. updated unit count)
    if (freshLocation.total_stock_units !== current.total_stock_units) {
      setSelectedLocation(prev => ({ ...prev, ...freshLocation }))
    }
  }, [locationsData])

  // Filter stock items by SKU or UPC search
  const filteredStockItems = useMemo(() => {
    if (!stockSummaryData?.stock_items) return []
    if (!appliedSkuFilter) return stockSummaryData.stock_items
    
    const searchLower = appliedSkuFilter.toLowerCase().trim()
    return stockSummaryData.stock_items.filter(item => {
      // Search in product_skus array
      if (item.product_skus && Array.isArray(item.product_skus)) {
        const matchesSku = item.product_skus.some(sku => 
          sku.toLowerCase().includes(searchLower)
        )
        if (matchesSku) return true
      }
      
      // Search in product_sku (fallback)
      if (item.product_sku?.toLowerCase().includes(searchLower)) {
        return true
      }
      
      // Search in product_upc
      if (item.product_upc?.toLowerCase().includes(searchLower)) {
        return true
      }
      
      return false
    })
  }, [stockSummaryData, appliedSkuFilter])

  // Group filtered stock items by store
  const stockItemsByStore = useMemo(() => {
    if (!filteredStockItems.length) return []
    
    const storeMap = new Map()
    
    filteredStockItems.forEach(item => {
      const storeId = item.store_id || 0
      const storeName = item.store_name || i18n.t('delete_lost_stock.unknown_store')
      
      if (!storeMap.has(storeId)) {
        storeMap.set(storeId, {
          store_id: storeId,
          store_name: storeName,
          items: [],
          total_units: 0
        })
      }
      
      const store = storeMap.get(storeId)
      store.items.push(item)
      store.total_units += item.quantity
    })
    
    // Convert to array and sort by store name
    return Array.from(storeMap.values()).sort((a, b) => 
      a.store_name.localeCompare(b.store_name)
    )
  }, [filteredStockItems, i18n])

  // Delete mutation
  const deleteMutation = useMutation(deleteLostStock, {
    onSuccess: (data) => {
      setConfirmDialogOpen(false)
      setNotificationData({
        type: 'success',
        title: i18n.t('delete_lost_stock.success_title'),
        description: i18n.t('delete_lost_stock.success_message', {
          count: data.deleted_count,
          location: data.unique_code
        })
      })
      setShowNotification(true)
      
      // Clear quantity for deleted item
      if (itemToDelete) {
        const key = getItemKey(itemToDelete)
        setQuantities(prev => {
          const newQuantities = { ...prev }
          delete newQuantities[key]
          return newQuantities
        })
      }
      
      // Refetch stock summary to update quantities
      refetchStockSummary()
      
      // Invalidate locations query to update counts
      queryClient.invalidateQueries(["lost-locations", warehouse_id])
      
      setItemToDelete(null)
    },
    onError: (error) => {
      setConfirmDialogOpen(false)
      const errorCode = error.response?.data?.error_code || 'INTERNAL_ERROR'
      const translationKey = ERROR_MESSAGES[errorCode] || 'delete_lost_stock.errors.internal_error'
      setNotificationData({
        type: 'error',
        title: i18n.t('delete_lost_stock.error_title'),
        description: i18n.t(translationKey)
      })
      setShowNotification(true)
      setItemToDelete(null)
    }
  })

  // Delete by store mutation
  const deleteStoreMutation = useMutation(deleteLostStockByStore, {
    onSuccess: (data) => {
      setConfirmStoreDialogOpen(false)
      setNotificationData({
        type: 'success',
        title: i18n.t('delete_lost_stock.success_title'),
        description: i18n.t('delete_lost_stock.success_message_store', {
          count: data.deleted_count,
          store: data.store_name,
          location: data.unique_code
        })
      })
      setShowNotification(true)
      refetchStockSummary()
      queryClient.invalidateQueries(["lost-locations", warehouse_id])
      setStoreToDelete(null)
    },
    onError: (error) => {
      setConfirmStoreDialogOpen(false)
      const errorCode = error.response?.data?.error_code || 'INTERNAL_ERROR'
      const translationKey = ERROR_MESSAGES[errorCode] || 'delete_lost_stock.errors.internal_error'
      setNotificationData({
        type: 'error',
        title: i18n.t('delete_lost_stock.error_title'),
        description: i18n.t(translationKey)
      })
      setShowNotification(true)
      setStoreToDelete(null)
    }
  })

  const handleDeleteStore = (store) => {
    setStoreToDelete(store)
    setConfirmStoreDialogOpen(true)
  }

  const handleConfirmDeleteStore = () => {
    if (!storeToDelete || !selectedLocation) return
    deleteStoreMutation.mutate({
      warehouse_id: parseInt(warehouse_id, 10),
      store_id: storeToDelete.store_id,
      unique_code: selectedLocation.unique_code
    })
  }

  // Helper functions
  const getItemKey = (item) => `${item.store_id}-${item.product_id}-${item.lot_id || 'no-lot'}`

  const handleLocationChange = (location) => {
    setSelectedLocation(location)
    setQuantities({})
    setSkuSearch('')
    setAppliedSkuFilter('')
  }

  const handleQuantityChange = (item, value) => {
    const key = getItemKey(item)
    const numValue = parseInt(value) || 0
    const maxValue = item.quantity
    
    setQuantities(prev => ({
      ...prev,
      [key]: Math.min(Math.max(0, numValue), maxValue)
    }))
  }

  const handleDeleteClick = (item) => {
    const key = getItemKey(item)
    const quantity = quantities[key]
    
    if (!quantity || quantity <= 0) {
      setNotificationData({
        type: 'error',
        title: i18n.t('delete_lost_stock.error_title'),
        description: i18n.t('delete_lost_stock.errors.no_quantity_selected')
      })
      setShowNotification(true)
      return
    }
    
    setItemToDelete({ ...item, deleteQuantity: quantity })
    setConfirmDialogOpen(true)
  }

  const handleConfirmDelete = () => {
    if (!itemToDelete || !selectedLocation) return

    const payload = {
      product_id: itemToDelete.product_id,
      warehouse_id: parseInt(warehouse_id, 10),
      unique_code: selectedLocation.unique_code,
      quantity: itemToDelete.deleteQuantity
    }
    
    if (itemToDelete.lot_id) {
      payload.lot_id = itemToDelete.lot_id
    }

    deleteMutation.mutate(payload)
  }

  const handleSearch = () => {
    setAppliedSkuFilter(skuSearch)
  }

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleClearSearch = () => {
    setSkuSearch('')
    setAppliedSkuFilter('')
    searchInputRef.current?.focus()
  }

  const goBack = () => {
    history.push(LOCATIONS.replace(':warehouse_id', warehouse_id))
  }

  // Format location options for SelectInput
  const locationOptions = useMemo(() => {
    if (!locationsData?.locations) return []
    return locationsData.locations.map(loc => ({
      ...loc,
      id: loc.id,
      name: `${loc.unique_code} (${loc.total_stock_units} ${i18n.t('delete_lost_stock.units')})`,
    }))
  }, [locationsData, i18n])

  // Format SKUs for display
  const formatSkus = (item) => {
    if (item.product_skus && Array.isArray(item.product_skus) && item.product_skus.length > 0) {
      return item.product_skus.join(', ')
    }
    return item.product_sku || '-'
  }

  // Render stock item card
  const renderStockItem = (item) => {
    const key = getItemKey(item)
    const currentQuantity = quantities[key] || ''
    
    return (
      <div 
        key={key}
        className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors bg-white"
      >
        <div className="flex items-start gap-4">
          {/* Product Image */}
          <div className="flex-shrink-0">
            {item.product_image_url ? (
              <img 
                src={item.product_image_url} 
                alt={item.product_name}
                className="w-16 h-16 object-cover rounded-md border border-gray-200"
              />
            ) : (
              <div className="w-16 h-16 bg-gray-100 rounded-md flex items-center justify-center">
                <span className="text-gray-400 text-xs">{i18n.t('delete_lost_stock.no_image')}</span>
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="flex-grow min-w-0">
            <h4 className="font-medium text-gray-900 truncate">
              {item.product_name}
            </h4>
            <div className="text-sm text-gray-500 space-y-0.5">
              <p className="truncate">
                <span className="font-medium">SKU:</span> {formatSkus(item)}
              </p>
              {item.product_upc && (
                <p>
                  <span className="font-medium">UPC:</span> {item.product_upc}
                </p>
              )}
            </div>
            {item.has_lot && (
              <div className="mt-1">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  {i18n.t('delete_lost_stock.labels.lot')}: {item.lot_name}
                </span>
                {item.lot_expiration_date && (
                  <span className="ml-2 text-xs text-gray-500">
                    {i18n.t('delete_lost_stock.labels.expires')}: {item.lot_expiration_date}
                  </span>
                )}
              </div>
            )}
            {!item.has_lot && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 mt-1">
                {i18n.t('delete_lost_stock.no_lot')}
              </span>
            )}
          </div>

          {/* Quantity Controls */}
          <div className="flex-shrink-0 flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm text-gray-500">
                {i18n.t('delete_lost_stock.labels.available')}
              </p>
              <p className="text-lg font-semibold text-gray-900">
                {item.quantity}
              </p>
            </div>
            
            <ChevronRightIcon className="h-5 w-5 text-gray-300" />
            
            <div>
              <label className="block text-sm text-gray-500 mb-1">
                {i18n.t('delete_lost_stock.labels.to_delete')}
              </label>
              <input
                type="number"
                min="0"
                max={item.quantity}
                value={currentQuantity}
                onChange={(e) => handleQuantityChange(item, e.target.value)}
                className="w-20 px-2 py-1 border border-gray-300 rounded-md text-center focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="0"
              />
            </div>

            <ButtonLoader
              type="danger"
              onClick={() => handleDeleteClick(item)}
              disabled={!currentQuantity || currentQuantity <= 0 || deleteMutation.isLoading}
              className="ml-2"
            >
              <TrashIcon className="h-4 w-4" />
            </ButtonLoader>
          </div>
        </div>
      </div>
    )
  }

  // Render no permission view
  if (!hasPermission) {
    return (
      <PageView
        topMenu={
          <PageTopBar>
            <div className="text-lg font-semibold">
              {i18n.t('delete_lost_stock.title')}
            </div>
          </PageTopBar>
        }
      >
        <div className="flex flex-col items-center justify-center py-12">
          <ExclamationIcon className="h-16 w-16 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {i18n.t('delete_lost_stock.no_permission_title')}
          </h2>
          <p className="text-gray-500 mb-6">
            {i18n.t('delete_lost_stock.no_permission_message')}
          </p>
          <ButtonLoader type="secondary" onClick={goBack}>
            {i18n.t('delete_lost_stock.go_back')}
          </ButtonLoader>
        </div>
      </PageView>
    )
  }

  return (
    <>
      <ConfirmDialog
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
        onCancel={() => {
          setConfirmDialogOpen(false)
          setItemToDelete(null)
        }}
        onConfirm={handleConfirmDelete}
        title={i18n.t('delete_lost_stock.confirm_title')}
        description={
          itemToDelete && (
            <div className="text-sm text-gray-500">
              <p className="mb-3">{i18n.t('delete_lost_stock.confirm_description')}</p>
              <div className="bg-gray-50 rounded-md p-3 space-y-2">
                <p><strong>{i18n.t('delete_lost_stock.labels.store')}:</strong> {itemToDelete.store_name}</p>
                <p><strong>{i18n.t('delete_lost_stock.labels.product')}:</strong> {itemToDelete.product_name}</p>
                <p><strong>SKU:</strong> {formatSkus(itemToDelete)}</p>
                {itemToDelete.product_upc && (
                  <p><strong>UPC:</strong> {itemToDelete.product_upc}</p>
                )}
                {itemToDelete.has_lot && (
                  <p><strong>{i18n.t('delete_lost_stock.labels.lot')}:</strong> {itemToDelete.lot_name}</p>
                )}
                <p><strong>{i18n.t('delete_lost_stock.labels.location')}:</strong> {selectedLocation?.unique_code}</p>
                <p><strong>{i18n.t('delete_lost_stock.labels.quantity_to_delete')}:</strong> <span className="text-red-600 font-semibold">{itemToDelete.deleteQuantity}</span></p>
              </div>
              <p className="mt-4 text-red-600 font-medium">
                {i18n.t('delete_lost_stock.confirm_warning')}
              </p>
            </div>
          )
        }
        confirmLabel={i18n.t('delete_lost_stock.confirm_button')}
        cancelLabel={i18n.t('delete_lost_stock.cancel_button')}
        loading={deleteMutation.isLoading}
        loadingLabel={i18n.t('delete_lost_stock.loading')}
        type="danger"
      />

      <ConfirmDialog
        open={confirmStoreDialogOpen}
        setOpen={setConfirmStoreDialogOpen}
        title={i18n.t('delete_lost_stock.confirm_store_title')}
        description={
          storeToDelete ? i18n.t('delete_lost_stock.confirm_store_description', {
            store: storeToDelete.store_name,
            location: selectedLocation?.unique_code,
            count: storeToDelete.total_units
          }) : ''
        }
        confirmLabel={i18n.t('delete_lost_stock.confirm_button')}
        cancelLabel={i18n.t('delete_lost_stock.cancel_button')}
        onConfirm={handleConfirmDeleteStore}
        onCancel={() => { setConfirmStoreDialogOpen(false); setStoreToDelete(null) }}
        loading={deleteStoreMutation.isLoading}
        loadingLabel={i18n.t('delete_lost_stock.loading')}
        type="danger"
      />

      <Notification
        show={showNotification}
        setShow={setShowNotification}
        title={notificationData.title}
        description={notificationData.description}
        type={notificationData.type}
      />

      <PageView
        topMenu={
          <PageTopBar>
            <div className="text-lg font-semibold">
              {i18n.t('delete_lost_stock.title')}
              <span className="text-sm text-gray-400 ml-2">
                {i18n.t('common.warehouse')} {warehouse_id}
              </span>
            </div>
            <div className="flex gap-2">
              <ButtonLoader type="secondary" onClick={() => history.push(DELETED_LOST_HISTORY.replace(':warehouse_id', warehouse_id))}>
                {i18n.t('delete_lost_stock.view_history')}
              </ButtonLoader>
              <ButtonLoader type="secondary" onClick={goBack}>
                {i18n.t('delete_lost_stock.go_back')}
              </ButtonLoader>
            </div>
          </PageTopBar>
        }
      >
        <div className="max-w-4xl mx-auto py-8 px-4">
          {/* Select Location */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {i18n.t('delete_lost_stock.step1_title')}
            </h3>

            {isLoadingLocations ? (
              <div className="flex justify-center py-4">
                <Loader show={true} />
              </div>
            ) : isLocationsError ? (
              <p className="text-red-500">{i18n.t('delete_lost_stock.errors.loading_locations')}</p>
            ) : locationOptions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">{i18n.t('delete_lost_stock.no_lost_locations')}</p>
              </div>
            ) : (
              <SelectInput
                label={i18n.t('delete_lost_stock.labels.select_location')}
                options={locationOptions}
                value={selectedLocation}
                onChange={handleLocationChange}
                className="max-w-md"
              />
            )}
          </div>

          {/* Select Products and Quantities (Grouped by Store) */}
          {selectedLocation && (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {i18n.t('delete_lost_stock.step2_title')}
                </h3>
                <button
                  type="button"
                  onClick={() => { refetchLocations(); refetchStockSummary() }}
                  disabled={isFetchingStockSummary}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshIcon className={`h-4 w-4 mr-1.5 ${isFetchingStockSummary ? 'animate-spin' : ''}`} />
                  {i18n.t('delete_lost_stock.refetch')}
                </button>
              </div>

              <div className="relative">
              {isFetchingStockSummary && !isLoadingStockSummary && (
                <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center rounded-md">
                  <Loader show={true} />
                </div>
              )}

              <p className="text-sm text-gray-500 mb-4">
                {i18n.t('delete_lost_stock.step2_description', { location: selectedLocation.unique_code })}
              </p>

              {/* SKU Search */}
              {!isLoadingStockSummary && !isStockSummaryError && stockSummaryData?.stock_items?.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {i18n.t('delete_lost_stock.search_by_sku')}
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-grow max-w-md flex items-center">
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={skuSearch}
                        onChange={(e) => setSkuSearch(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder={i18n.t('delete_lost_stock.search_placeholder')}
                        className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      {appliedSkuFilter && (
                        <button
                          type="button"
                          onClick={handleClearSearch}
                          className="absolute right-3 inset-y-0 flex items-center text-gray-400 hover:text-gray-600"
                        >
                          <XIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleSearch}
                      disabled={!skuSearch.trim()}
                      className={`inline-flex items-center justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:text-sm ${
                        !skuSearch.trim() 
                          ? 'bg-gray-300 cursor-not-allowed' 
                          : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                      }`}
                    >
                      <SearchIcon className="h-4 w-4 mr-1" />
                      {i18n.t('delete_lost_stock.search_button')}
                    </button>
                  </div>
                  {appliedSkuFilter && (
                    <p className="mt-2 text-sm text-gray-500">
                      {i18n.t('delete_lost_stock.search_results', { 
                        count: filteredStockItems.length,
                        term: appliedSkuFilter 
                      })}
                    </p>
                  )}
                </div>
              )}

              {isLoadingStockSummary ? (
                <div className="flex justify-center py-8">
                  <Loader show={true} />
                </div>
              ) : isStockSummaryError ? (
                <p className="text-red-500">{i18n.t('delete_lost_stock.errors.loading_stock')}</p>
              ) : stockItemsByStore.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    {appliedSkuFilter 
                      ? i18n.t('delete_lost_stock.no_search_results')
                      : i18n.t('delete_lost_stock.no_stock_items')
                    }
                  </p>
                  {appliedSkuFilter && (
                    <button
                      onClick={handleClearSearch}
                      className="mt-2 text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                    >
                      {i18n.t('delete_lost_stock.clear_search')}
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {stockItemsByStore.map((store) => (
                    <div key={store.store_id} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Store Header */}
                      <div
                        className="bg-gray-50 px-4 py-3 border-b border-gray-200 cursor-pointer select-none hover:bg-gray-100 transition-colors"
                        onClick={() => setExpandedStores(prev => ({ ...prev, [store.store_id]: !prev[store.store_id] }))}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <ChevronRightIcon className={`h-4 w-4 text-gray-400 mr-2 transition-transform ${expandedStores[store.store_id] ? 'rotate-90' : ''}`} />
                            <OfficeBuildingIcon className="h-5 w-5 text-gray-500 mr-2" />
                            <h4 className="font-semibold text-gray-900">
                              {store.store_name}
                            </h4>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-500">
                              {store.total_units} {i18n.t('delete_lost_stock.units')}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleDeleteStore(store) }}
                              disabled={deleteStoreMutation.isLoading}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors"
                            >
                              <TrashIcon className="h-3 w-3 mr-1" />
                              {i18n.t('delete_lost_stock.delete_all_store')}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Store Items */}
                      {expandedStores[store.store_id] && (
                        <div className="p-4 space-y-3 bg-gray-50/50">
                          {store.items.map(item => renderStockItem(item))}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Total Summary */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">
                        {appliedSkuFilter 
                          ? i18n.t('delete_lost_stock.filtered_total')
                          : i18n.t('delete_lost_stock.total_in_location')
                        }:
                      </span>
                      <span className="font-semibold text-gray-900">
                        {filteredStockItems.reduce((sum, item) => sum + item.quantity, 0)} {i18n.t('delete_lost_stock.units')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm mt-1">
                      <span className="text-gray-500">
                        {i18n.t('delete_lost_stock.total_stores')}:
                      </span>
                      <span className="font-semibold text-gray-900">
                        {stockItemsByStore.length}
                      </span>
                    </div>
                    {appliedSkuFilter && (
                      <div className="flex justify-between items-center text-sm mt-1">
                        <span className="text-gray-500">
                          {i18n.t('delete_lost_stock.total_in_location')}:
                        </span>
                        <span className="font-semibold text-gray-900">
                          {stockSummaryData?.total_stock_units} {i18n.t('delete_lost_stock.units')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>
            </div>
          )}
        </div>
      </PageView>
    </>
  )
}
